import { AppContext } from "App";

import { createSetCanValueLoadAction, createSetInteractiveAction } from "@multiweb/frames";
import { ReactConnectorFramesManager } from "@multiweb/reactConnectors/ReactConnectorFramesManager";
import { bridge } from "@multiweb/reactCore/bridge";
import { createDeleteDataSourceAction, createRefreshAction } from "@multiweb/reactCore/datasource";
import { StoreManager } from "@multiweb/reactSetup";
import { IconCode } from "@multiweb/types/contracts";
import { ActionType, CancellationToken, RequestContext } from "@multiweb/types/multiweb";
import { ReactAction, ReactActionId } from "@multiweb/types/react";
import { DataConverter } from "Global/Base/Converters/DataConverter";
import { Event, IListener, StatefulEvent } from "Global/Base/Events";
import { InvalidOperationException } from "Global/Base/Exceptions";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { IImplicitFormInfo } from "OW.Remoting/Remoting/OWManagerInterfaces";
import { GenericControl } from "UI/UI.BaseControls/GenericControl";
import { ActionContext, IAction, IActionContext, IMultiObjectAction } from "UI/UI.Controls/Actions/IAction";
import { Control } from "UI/UI/UI/Controls/Control";
import { ControlParams, IHTMLElementWithControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { IFRMParams } from "UI/UI/UI/Controls/FormParameters";
import { Registrator } from "UI/UI/UI/Controls/Registrator";
import { FormManager } from "UI/UI/UI/FormManager/IFormManager";
import { IMenu } from "UI/UI/UI/FormUI/FormUI";
import { WindowState } from "UI/UI/UI/UIManagerInterfaces";
import { OpenModeType } from "UI/UI/UI/XGControls/Actions/DetailFormAction";
import { XGAction } from "UI/UI/UI/XGControls/Actions/XGAction";
import { HideButtons, IActualObjectChangedEventArgs } from "UI/UI/UI/XGControls/Frames";
import { FrameSharedMethods } from "UI/UI/UI/XGControls/Frames/FrameSharedMethods";
import { MasterableFormControl } from "UI/UI/UI/XGControls/Frames/MasterableFormControl";
import { IForm } from "UI/UI/UI/XGControls/IForm";
import { IFrame, IFrameCtxMenuData } from "UI/UI/UI/XGControls/IFrame";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { IMasterableFrame } from "../../UI/UI/XGControls/Frames/FrameInterfaces";
import { reactControlConnectorLogger } from "../logger";
import { IReactFrameConnector } from "../types";

const { dispatchAction } = bridge;

/*
* //TODO: #33782 GFC by měl dědit z obecného Control. Věci ohledně masterFramu se získají kompozicí mixiny HasMasterFrame.
* GRC by o masterovi neměl vůbec vědět. V rámci #25888 se změnil dočasně předek na MasterableFormControl,
* je potřeba to ale vyřešit správně v tasku #33782
*/
/**
 * Slouží k propojení stávající aplikace a Frame.
 */
export class GeneralFrameConnector extends MasterableFormControl implements IReactFrameConnector {
    //#region Properties
    public readonly uuid: string;
    /**
     * Zda je frame hlavním framem.
     */
    public isMainFrame: boolean = true;
    /**
     * Název framu, musí být unikátní na formuláři, slouží k provázání framu.
     */
    public frameName: string | undefined = undefined;
    /**
     * Právě vybraný/zobrazený doménový objekt ve framu.
     */
    public actualObject: OWObject | undefined = undefined;
    /**
     * Zda se má do seznamu přidávat 3dot akce.
     */
    public renderStandardRowActions: boolean = false;
    /**
     * Událost vyvolaná při změně hodnoty v aktuálním objektu.
     */
    public readonly actualObjectValueChanged: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();
    /**
     * Zda je frame zobrazen jen pro čtení.
     */
    public readOnly: boolean = false;
    /**
     * Zda je frame aktivní a lze s ním pracovat a pouštět nad ním akce (musí být povolen právy a nesmí být ve stavu jen ke čtení).
     */
    public isActive: boolean = true;
    /**
     * Skryté akce.
     */
    public hideButtons: HideButtons | undefined = undefined;
    /**
     * Po zaktivnění formu dojde k refreshi aktuálního objektu.
     */
    public refreshObjectAfterSetActive: boolean = false;
    /**
     * Po zaktivnění formu dojde k refreshi aktuálního framu.
     */
    public refreshListAfterSetActive: boolean = false;
    /**
     * Událost oznamující zviditelnění framu.
     */
    public frameIsVisible: Event<IFrame, void> = new Event<IFrame, void>();
    /**
     * Událost vyvolaná při otevírání kontextového menu ve framu.
     */
    public contextMenuOpening: Event<IFrame, IFrameCtxMenuData> = new Event<IFrame, IFrameCtxMenuData>();
    /**
     * Událost, která nastane při změně aktuálně vybraného objektu.
     */
    public readonly actualObjectChanged: Event<IFrame, IActualObjectChangedEventArgs> = new Event<
        IFrame,
        IActualObjectChangedEventArgs
    >();
    /**
     * Událost vyvolaná při dokončení inicializace framu.
     */
    public readonly initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();
    /**
     * Informace o tom, zda má být skrytý toolbar.
     */
    public hideToolBar: boolean = false;
    /**
     * Toolbar.
     */
    public menu: IMenu | undefined = undefined;
    /**
     * Obsahuje master object.
     */
    public masterObject: OWObject | undefined = undefined;
    /**
     * Název nadřízeného framu, musí se shodovat s frameName jiného framu nebo bude prázdný.
     */
    public masterFrameName: string | null | undefined = undefined;
    /**
     * Název vazby na nadřízený objekt.
     */
    public masterLinkToAttr: string | null | undefined = undefined;
    /**
     * Název vazby z nadřízeného objektu, pokud není vyplněn použije se původně vybraný objekt.
     */
    public masterAttr: string | null | undefined = undefined;
    /**
     * Příznak, zda probíhá změna objektu a vyhodnocení jeho práv.
     */
    public readonly actualObjectChangeRunning: boolean = false;
    /**
     * Potlačuje zobrazení YesNoCancel dialogu.
     */
    public readonly suppressSaveDialog: boolean = false;
    /**
     * @inheritDoc
     */
    public readonly detailFormName: string;
    /**
     * Událost, která nastane v momentě, kdy frame má připravené akce pro aktuálně vybraný objekt.
     */
    public readonly contextActionsReady: Event<IMasterableFrame, string> = new Event<IMasterableFrame, string>();
    public interactive: boolean = true;

    private _initializationStarted: boolean = false;

    public get initializationStarted(): boolean {
        return this._initializationStarted;
    }

    private _isInitialized: boolean = false;
    /**
     * Příznak, zda již byl frame inicializován.
     */
    public get isInitialized(): boolean {
        return this._isInitialized;
    }
    /**
     * Příznak že na framu probíhá načítání objektu.
     */
    public readonly isSetActualObjectRunning?: boolean;
    /**
     * Režim otevření formuláře (z definice formuláře).
     */
    public openMode: OpenModeType = OpenModeType.standard;
    /**
     * @inheritDoc
     */
    public loadOnScroll: boolean = true;
    /**
     * Akce pro aktualizaci dat.
     */
    private _refreshAction: IAction | undefined = undefined;
    /**
     * Akce, které lze spustit nad aplikačním záznamem.
     */
    private _objectActions: Array<IAction> = new Array<IAction>();
    /**
     * Definice akcí.
     */
    private _definitionActionElements: IHTMLElementWithControl[];

    /**
     * @inheritDoc
     */
    public get objectActions(): ReadonlyArray<IAction> {
        return this._objectActions;
    }

    public reactContainer: HTMLElement | undefined;
    private _captionElement: HTMLElement;
    protected _showCaption: boolean = false;

    //#endregion Properties

    //#region Listeners
    protected _canValueLoadChangedListener: IListener | undefined;
    protected _ownerFormStateChangedListener: IListener | undefined;
    protected _readOnlyChangedListener: IListener | undefined;
    protected _framesInitializedListener: IListener | undefined;
    //#endregion Listeners

    constructor(params: ControlParams) {
        super({
            ...params,
            ownerForm: (GenericControl
                .findParent(params.element, "[data-behaviour=form]") as IHTMLElementWithControl).tscControl as IForm,
        });

        ReactConnectorFramesManager.register(this);

        this.frameName = this.element.getAttribute("data-framename");
        this.objectName = this.element.getAttribute("data-objectname");
        this.detailFormName = this.element.getAttribute("data-detailform");
        if (!(this.ownerForm instanceof IForm))
            throw new Error("IFrame component must be inside IForm component");

        this.hideToolBar = DataConverter.toBoolean(this.element.getAttribute("data-hidetoolbar"), false);
        this.element.className = "react-frame-connector";
        this._showCaption = DataConverter.toBoolean(this.element.getAttribute("data-showcaption"), false);

        this._definitionActionElements = Array.from(
            this.element.querySelectorAll("[data-behaviour='action']"),
        ) as IHTMLElementWithControl[];

        this.canValueLoadChanged.add(this._canValueLoadChangedListener = (_s: Control, value: boolean) => dispatchAction(
            createSetCanValueLoadAction(this.element.id, value),
        ));

        this.ownerForm.readOnlyChanged.add(this._readOnlyChangedListener = (_s: IForm, value: boolean) => {
            dispatchAction(createSetInteractiveAction(this.element.id, !value));
            this.setToolbarActionsEnabledVisual(!value);
        });

        this._getOpenModeAttribute();
        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.frameName);
    }

    public async initialize(): Promise<void> {
        FrameSharedMethods.runWhenFirstShown(this, () => this.initializeVisual());
    }

    /**
     * @inheritDoc
     */
    public async initializeVisual(): Promise<void> {
        this._initializationStarted = true;
        await this._initializeFormUI();

        // Zpracování a zaregistrování akcí do menu.
        await this.initializeActions();

        this.ownerForm.stateChanged.add(this._ownerFormStateChangedListener = (_ownerForm, { state, value }) => {
            if (state === WindowState.visible) {
                this.visible = value; // TODO: RightManager visibility
            }
        });

        this.reactContainer = document.createElement("div");
        this.reactContainer.classList.add("react-frame-connector-container");
        if (!this.hideToolBar) {
            this.reactContainer.classList.add("react-frame-connector-container--withtoolbar");
        }
        this.element.appendChild(this.reactContainer);
        StoreManager.initializeStore(!!AppContext.instance.configuration.developmentMode);

        this._isInitialized = true;
        this.initialized.trigger(this, undefined);
    }

    public async _initializeFormUI(): Promise<void> {
        if (!this.menu)
            // Pokud už je menu nastaveno, nevytváří se nové. Tato možnost zavedena pro TreeFrame, kde se do menu GLF přiřadí menu TF již před inicializací GLF.
            this.menu = this.ownerForm.formUI.createFrameMenu(this);

        if (!this.menu) throw new InvalidOperationException("Unable to create frame menu");
        // toolbar musí být vidět, pokud není nastaveno, aby byl skrytý a zároveň je vidět seznam nebo seznam má detail, který je vidět.
        this.menu.setVisibility(true);
        this._createFrameCaption();
    }

    /**
     * @inheritDoc
     */
    public async initializeActions(): Promise<void> {
        await this.addCustomActions();
    }

    /**
     * @inheritDoc
     */
    public setRefreshActionVisibility(isVisible: boolean): void {
        this._refreshAction.visible = isVisible;
    }

    /**
     * Znovunačtení všech zobrazených dat.
     *
     * @param _manualRefresh značí, zda je akce vyvolaná uživatelem (zabraňuje nebo povoluje označeni řádku v seznamu).
     */
    public async refreshData(_manualRefresh: boolean): Promise<void> {
        dispatchAction(createRefreshAction(this.element.id));
    }
    /**
     * Obnoví právě vybraný doménový objekt.
     */
    public async refreshActualObject(): Promise<void> {
        throw new Error("[GeneralFrameConnector] not implemented yet");
    }
    /**
     * Čeká na dočtení dat.
     */
    public async waitForDataLoad(): Promise<boolean> {
        return true;
    }
    /**
     * Čeká na doběhnutí změn dat.
     */
    public async waitForPendingUpdates(): Promise<void> {
        throw new Error("[GeneralFrameConnector] not implemented yet");
    }
    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu.
     */
    public async waitForActualObjectSet(): Promise<boolean> {
        return true;
    }

    protected _getOpenModeAttribute(): void {
        this.openMode = FrameSharedMethods.getOpenModeTypeFromAttribute(this.element, true);
    }

    /**
     * Pokud má frame lokalizační klíč, vytvoří a vloží do stránky caption element.
     */
    protected _createFrameCaption(): void {
        if (this.locKey && !this.locKey.endsWith("-666")) {
            this._captionElement = FrameSharedMethods.createFrameCaptionElement(this.locKey);
            this.element.insertBefore(this._captionElement, this.element.firstElementChild);
            this._setFrameCaptionVisibility(this._showCaption);
        }
    }

    /**
     * Podle showCaption nastavuje viditelnost elementu frame-caption.
     */
    protected _setFrameCaptionVisibility(value: boolean): void {
        this._captionElement?.classList.toggle("hidden", !value);
    }

    /**
     * @inheritDoc
     */
    public executeAction(actionId: ReactActionId, ids: string[], className: string): void {
        this._objectActions.find(action => action.id === actionId)?.execute({ ids, className });
    }

    /**
     * @inheritDoc
     */
    public getReactRowActions(): ReactAction[] {
        return this._objectActions.map((action, _index) => ({
            id: action.id,
            locKey: action.locKey,
            actionType: action.actionType,
            enabled: action.enabled,
            enabledVisual: action.enabledVisual,
            order: action.order,
            priority: action.priority,
            visible: action.visible,
            isActive: action.isActive,
            actionSize: action.actionSize,
            text: "",
            iconCode: EnumUtils.hasEnumValue(IconCode, action.icon) ? (action.icon as IconCode) : undefined,
            isInNode: action.isInNode,
            masterNode: action.masterNode,
            extendedProperties: action.extendedProperties,
        }));
    }

    public _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }

    /**
     * @inheritDoc
     */
    public async openDetailForm(dataObjectId: string): Promise<void> {
        if (!this.detailFormName) {
            reactControlConnectorLogger.warn("Frame can not open detail form without 'detailFormName'.");
            return;
        }
        const context: IActionContext = {
            ids: [dataObjectId],
            className: this.objectName,
        };
        const formInfo: IImplicitFormInfo = await FrameSharedMethods.getDefaultFormName(
            this.detailFormName,
            this.ownerForm,
            context,
            this.frameContext,
            this.masterAttr,
        );
        const detailObject = await OWManager.getObject(context.ids[0], context.className, null, true, false, this._getRequestContext());

        let masterObject: OWObject = detailObject;
        const masterAttr = formInfo.MasterAttr;
        if (masterAttr != null) {
            await OWManager.publishInstance(detailObject.instanceID, masterAttr);
            masterObject = AttrValueReader.read(await detailObject.getValue(masterAttr));
        }

        const params: IFRMParams = {
            detailFormObject: detailObject,
            ownerForm: this.ownerForm,
            hideWindowActions: true,
            openMode: this.openMode,
            openInEdit: this.openMode !== OpenModeType.standard,
            viewMode: false,
            detailFormMasterFrame: this instanceof IMasterableFrame ? this : undefined,
            masterObject: formInfo.DetailAssocName && masterObject, // Předávat jen pokud je k dispozici DetailAssocName
            masterLinkToAttr: formInfo.DetailAssocName,
            isImplicit: FrameSharedMethods.isFormNameImplicit(this.detailFormName),
            formContext: this.ownerForm.formContext,
            transactionContextGetter: this.frameContext?.getTransactionContextId,
        };
        FormManager.instance.openForm(formInfo.FormName, params);
    }
    public async addCustomActions(): Promise<void> {
        const definitionActions = [];
        for (const actionElement of this._definitionActionElements) {
            if (!actionElement.tscControl) {
                //Pokud není inicializována, tak se ji pokusí znovu inicializovat.
                await Registrator.initializeControls(actionElement);
            }

            if (!actionElement.tscControl) continue;

            const action = actionElement.tscControl; //Vytáhne objekt typu action z html elementu.
            if (!(action instanceof XGAction)) {
                throw new InvalidOperationException(`Action id ${actionElement.id} is not an instance of XGAction`);
            }
            definitionActions.push(action);
        }

        for (const action of definitionActions) {
            if (action instanceof XGAction) {
                action.owner = this;
                this.menu.addAction(action);
                action.visible = action.enabled = true; // LLF nepodporuje práva. Proto jsou všechny akce již od začátku viditelné.
                action.enabledVisual = !this.ownerForm.readOnly;
            }
        }

        this._refreshAction = FrameSharedMethods.createStandardAction(
            ActionType.standardRefreshList,
            async () => {
                this.refreshData(false);
            },
            this,
        );
        this._refreshAction.visible = this._refreshAction.enabled = true;
        this._refreshAction.enabledVisual = !this.ownerForm.readOnly;
        this.menu.addAction(this._refreshAction);

        this._objectActions = [...this.menu.getActionsByContext(ActionContext.objectAction)];
    }

    /**
     * Nastaví viditelnost akcí v toolbaru.
     * @param value Hodnota, zda je akce viditelná.
     */
    public setToolbarActionsEnabledVisual(value: boolean): void {
        if (this.menu) {
            [...this.menu.getActionsByContext(ActionContext.frameAction)].forEach(action => {
                this.setToolbarActionEnabledVisual(action, value);
            });
        }
    }

    /**
     * @inheritDoc
     */
    public setToolbarActionEnabledVisual(action: IAction, value: boolean): void {
        if (!(action instanceof IMultiObjectAction)) {
            action.enabledVisual = value;
        }
    }

    public override dispose(): void {
        if (this._isDisposed) {
            return;
        }
        dispatchAction(createDeleteDataSourceAction(this.element.id));
        this.canValueLoadChanged.remove(this._canValueLoadChangedListener);
        this.ownerForm.readOnlyChanged.remove(this._readOnlyChangedListener);
        this.ownerForm.stateChanged.remove(this._ownerFormStateChangedListener);
        this.ownerForm.framesInitialized.remove(this._framesInitializedListener);
        ReactConnectorFramesManager.unregister(this);
        this.rollbackContext();
        super.dispose();
    }
    //#endregion
}
